import { ILink } from '@kontent-ai/delivery-sdk'

export interface ParsedGlossaryLink {
	itemId: string
	codename: string
	type: string
}

/**
 * Validates if a string is a non-empty UUID format
 */
function isValidUUID(str: string): boolean {
	if (!str || typeof str !== 'string') return false
	const uuidRegex =
		/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
	return uuidRegex.test(str)
}

/**
 * Validates if a string is a non-empty codename
 */
function isValidCodename(str: string): boolean {
	return typeof str === 'string' && str.trim().length > 0
}

/**
 * Parses HTML string to extract custom glossary links that match validation criteria
 *
 * Validation Conditions (Must match ALL):
 * 1. Tag is <a>
 * 2. Has class "glossary-link"
 * 3. Has data-item-type="link_glossary"
 * 4. Has valid data-item-id (non-empty UUID)
 * 5. Has valid data-item-codename (non-empty string)
 *
 * @param htmlString - Raw HTML string from rich text element
 * @returns Array of parsed glossary link data
 */
export function parseGlossaryLinksFromHTML(
	htmlString: string,
): ParsedGlossaryLink[] {
	if (!htmlString || typeof htmlString !== 'string') {
		return []
	}

	try {
		// Create a temporary DOM element to parse HTML
		const tempDiv = document.createElement('div')
		tempDiv.innerHTML = htmlString

		// Find all anchor tags with class "glossary-link"
		const glossaryLinks = tempDiv.querySelectorAll('a.glossary-link')

		const parsedLinks: ParsedGlossaryLink[] = []

		glossaryLinks.forEach((link) => {
			const itemType = link.getAttribute('data-item-type')
			const itemId = link.getAttribute('data-item-id')
			const codename = link.getAttribute('data-item-codename')

			// Validate all required conditions
			if (
				itemType === 'link_glossary' &&
				itemId &&
				isValidUUID(itemId) &&
				codename &&
				isValidCodename(codename)
			) {
				parsedLinks.push({
					itemId,
					codename,
					type: itemType,
				})
			}
		})

		return parsedLinks
	} catch (error) {
		console.error('Error parsing glossary links from HTML:', error)
		return []
	}
}

/**
 * Transforms parsed glossary link data to ILink objects compatible with Kontent.ai
 *
 * @param parsedLinks - Array of parsed glossary link data
 * @returns Array of ILink objects
 */
export function transformToILinks(parsedLinks: ParsedGlossaryLink[]): ILink[] {
	return parsedLinks.map((link) => ({
		linkId: link.itemId,
		codename: link.codename,
		type: link.type,
		urlSlug: '',
	}))
}

/**
 * Combined function: Parse HTML and transform to ILink objects in one step
 *
 * @param htmlString - Raw HTML string from rich text element
 * @returns Array of ILink objects ready to be merged with existing links
 */
export function extractGlossaryILinksFromHTML(htmlString: string): ILink[] {
	const parsedLinks = parseGlossaryLinksFromHTML(htmlString)
	return transformToILinks(parsedLinks)
}
